import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { AppDispatch, RootStateType } from '@Store';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import SBDropdown from '@Components/Dropdown';
import { useFormik } from 'formik';
import SbButton from '@Components/Button';
// import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { ApiServiceCodeOptions, ProductCodeOptions, TermsOfTradeOptions, onForward } from './constants';
import * as Yup from 'yup';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';

interface propsTypes {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showFedExDetails: boolean;
  btnText: string;
}

const universalInformationSchema = Yup.object().shape({
  type: Yup.string().trim().required('Type is required'),
  accountNo: Yup.mixed()
    .test('is-valid', 'Account is required', value => {
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object') return true;
      return !!value;
    })
    .required('Account is required'),
  apiServiceCode: Yup.string().trim().required('API Service Code is required'),
  productCode: Yup.string().trim().required('Product Code is required'),
  termsOfTrade: Yup.string().trim().required('Terms of Trade is required'),
});

const UserUniversal = (props: propsTypes) => {
  const { onClickForward, onClose, initialStates, isEditable, showFedExDetails, btnText } = props;
  const dispatch = useDispatch<AppDispatch>();
  const { universalDetails } = useSelector((state: RootStateType) => state.adminShipment);
  const invoiceNo = useSelector((state: RootStateType) => state.shipment.shipmentById?._packageId.invoiceNumber);
  const { shipmentById, userUniversalInformationDetails: universalSettings } = useSelector(
    (state: RootStateType) => state.shipment,
  );

  const { values, setFieldValue, errors, handleSubmit } = useFormik<any>({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      ...initialStates,
      invoiceNumber: invoiceNo,
      accountNo: universalSettings?.accountNo
        ? universalSettings?.accountNo.filter((acc: any) => acc.value.startsWith(initialStates.accountNo))
        : [],
    },
    validationSchema: universalInformationSchema,
    onSubmit(values) {
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  return (
    <>
      {showFedExDetails && (
        <Paper
          variant="outlined"
          sx={{
            my: '2rem',
            p: '2rem',
          }}
        >
          <Grid container item xs={12}>
            <Grid container item mb={3}>
              <Grid item xs={12} md={6}>
                <Typography className="font-medium l-text pb-3">Universal Information</Typography>{' '}
              </Grid>
            </Grid>
            <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="accountNo"
                    label="Universal Account"
                    required
                    placeholder="Select"
                    name="accountNo"
                    value={
                      Array.isArray(values?.accountNo)
                        ? values.accountNo.map((item: { value: any }) => item.value)
                        : values.accountNo
                        ? [values.accountNo]
                        : []
                    }
                    options={universalSettings?.accountNo ?? []}
                    onChange={(_, selectedObj) => {
                      setFieldValue('accountNo', selectedObj);
                    }}
                    error={errors.accountNo as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="apiServiceCode"
                    label="Service Code"
                    required
                    placeholder="Select"
                    name="apiServiceCode"
                    value={values.apiServiceCode ? [values.apiServiceCode] : []}
                    options={
                      universalSettings?.apiServiceCode?.length
                        ? universalSettings.apiServiceCode
                        : ApiServiceCodeOptions
                    }
                    onChange={val => setFieldValue('apiServiceCode', val[0] || '')}
                    error={errors.apiServiceCode as string}
                  />
                </Grid>
              </Grid>
              <Grid container item columnSpacing={10} rowSpacing={2}>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="productCode"
                    label="Product Code"
                    required
                    placeholder="Select"
                    name="productCode"
                    value={values.productCode ? [values.productCode] : []}
                    options={
                      universalSettings?.productCode?.length ? universalSettings.productCode : ProductCodeOptions
                    }
                    onChange={val => {
                      const selected = val[0] || '';
                      setFieldValue('productCode', selected);

                      // ✅ Set other fields conditionally
                      if (selected === 'NONDOX') {
                        setFieldValue('freeFormCurrency', 1);
                        setFieldValue('newDocketFreeFormInvoice', 1);
                      } else {
                        setFieldValue('freeFormCurrency', 0);
                        setFieldValue('newDocketFreeFormInvoice', 0);
                      }
                    }}
                    error={errors.productCode as string}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SBDropdown
                    disabled={!isEditable}
                    id="termsOfTrade"
                    label="Terms of Trade"
                    required
                    placeholder="Select"
                    name="termsOfTrade"
                    value={values.termsOfTrade ? [values.termsOfTrade] : []}
                    options={
                      universalSettings?.termsOfTrade?.length ? universalSettings.termsOfTrade : TermsOfTradeOptions
                    }
                    onChange={val => setFieldValue('termsOfTrade', val[0] || '')}
                    error={errors.termsOfTrade as string}
                  />
                </Grid>
              </Grid>

              <Divider className="mt-3 mb-3" />
              <Grid container item xs={12} md={12} lg={12} columnSpacing={4} rowSpacing={4}>
                <Grid container item sx={{ gap: '1rem' }}>
                  <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                    <SbButton
                      disabled={!isEditable}
                      variant="contained"
                      sx={{ borderRadius: '4px !important' }}
                      className="w-full"
                      onClick={_event => {
                        handleSubmit();
                      }}
                    >
                      {btnText}
                    </SbButton>
                  </Grid>
                  {onClose && (
                    <Grid item xs={12} sm={5} md={2.6} lg={2.7}>
                      <SbButton
                        sx={{ borderRadius: '4px !important' }}
                        className="w-full"
                        variant="outlined"
                        onClick={onClose}
                      >
                        Cancel
                      </SbButton>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}
      {universalDetails?.response && (
        <Grid container item>
          <Grid item xs={'auto'} border={'1px solid'} className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component={'span'}
              className="font-medium md-text ml-1"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById?.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default UserUniversal;
